import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Game } from '../types/game';
import { useAuth } from '../context/AuthContext';
import './GameCard.css';

interface GameCardProps {
  game: Game;
  onFavoriteToggle?: (gameId: string) => void;
}

const GameCard: React.FC<GameCardProps> = ({ game, onFavoriteToggle }) => {
  const navigate = useNavigate();
  const { canPlayGame, openLoginForGame } = useAuth();

  const handlePlay = () => {
    if (canPlayGame() && game.gameUrl) {
      navigate(`/play/${game.id}`);
      return;
    }
    openLoginForGame(game);
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFavoriteToggle) {
      onFavoriteToggle(game.id);
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="star filled">★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className="star half">★</span>);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<span key={i} className="star">★</span>);
    }
    return stars;
  };

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Get color for gradient based on game title
  const getColorFromTitle = (title: string): string => {
    const colors = ['FF4444', 'FF0000', '00CCFF', 'FFD700', 'FFFF00', '00FFFF', '0000FF', '008000', '800080', '000080', '808080', '4B0082', '8B0000', '2F4F4F', 'FF1493'];
    const index = title.length % colors.length;
    return colors[index];
  };

  return (
    <div className="game-card" onClick={handlePlay}>
      <div className="game-image-container">
        {!imageError ? (
          <img 
            src={game.image} 
            alt={game.title} 
            className={`game-image ${imageLoaded ? 'loaded' : ''}`}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        ) : (
          <div 
            className="game-image-placeholder"
            style={{
              background: `linear-gradient(135deg, #${getColorFromTitle(game.title)} 0%, #${getColorFromTitle(game.title)}80 100%)`
            }}
          >
            <span className="placeholder-text">{game.title}</span>
          </div>
        )}
        <button
          className={`favorite-button ${game.isFavorite ? 'active' : ''}`}
          onClick={handleFavorite}
          aria-label="Add to favorites"
        >
          ❤️
        </button>
        <div className="game-overlay">
          <button className="play-button" onClick={(e) => {
            e.stopPropagation();
            handlePlay();
          }}>
            PLAY
          </button>
        </div>
      </div>
      <div className="game-info">
        <h3 className="game-title">{game.title}</h3>
        <div className="game-rating">
          <div className="stars">{renderStars(game.rating)}</div>
          <span className="rating-text">{game.rating}/5</span>
        </div>
      </div>
    </div>
  );
};

export default GameCard;

